import React from "react";
import { Check, AlertTriangle } from "lucide-react";

// Shared sticky bottom status bar used by the Experience Mode flows.
// Always visible; reflects the pipeline outcome: loading | ready | failed.
// `action` renders an ink button on the right (e.g. "View ERA" / "Retry").
const TONE = {
  ready: {
    border: "border-[rgba(52,199,89,.4)]",
    bg: "linear-gradient(135deg, rgba(52,199,89,.18), rgba(52,199,89,.06))",
    icon: "bg-[var(--green,#34c759)]",
    iconShadow: "0 6px 16px rgba(52,199,89,.4)",
    chip: "bg-[rgba(52,199,89,.16)] text-[var(--green-deep,#248a3d)] border-[rgba(52,199,89,.3)]",
    Icon: Check,
    iconProps: { size: 22, strokeWidth: 3 },
  },
  failed: {
    border: "border-[rgba(255,159,10,.4)]",
    bg: "linear-gradient(135deg, rgba(255,159,10,.16), rgba(255,159,10,.05))",
    icon: "bg-[var(--amber,#ff9f0a)]",
    iconShadow: "0 6px 16px rgba(255,159,10,.4)",
    chip: "bg-[rgba(255,159,10,.16)] text-[var(--amber-deep,#a15c00)] border-[rgba(255,159,10,.3)]",
    Icon: AlertTriangle,
    iconProps: { size: 20, strokeWidth: 2.4 },
  },
};

export default function StatusFooter({ state = "loading", eyebrow, title, subtitle, action, testId = "status-footer", innerTestId }) {
  return (
    <div className="sticky bottom-0 z-20 -mx-6 -mb-4 mt-4 px-6 pt-3 pb-4 border-t border-black/[.08] bg-white" data-testid={testId}>
      {state === "loading" ? (
        <div className="rounded-[16px] border border-black/[.1] p-4 bg-white" style={{ boxShadow: "0 12px 34px rgba(0,0,0,.08)" }} data-testid={innerTestId}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
              <span className="w-[18px] h-[18px] rounded-full border-2 border-white/25 border-t-white animate-spin" />
            </div>
            <div className="flex-1 min-w-0">
              <div key={title} className="text-[15px] font-bold text-[var(--ink)] tracking-[-0.2px]">{title}</div>
              {subtitle ? <div key={subtitle} className="text-[10.5px] text-[var(--gray2)]">{subtitle}</div> : null}
            </div>
          </div>
        </div>
      ) : (
        (() => {
          const t = TONE[state] || TONE.failed;
          const Ic = t.Icon;
          return (
            <div className={`rounded-[16px] border-[1.5px] ${t.border} p-4`} style={{ background: t.bg, boxShadow: "0 12px 34px rgba(0,0,0,.12)" }} data-testid={innerTestId}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-full ${t.icon} text-white flex items-center justify-center flex-shrink-0`} style={{ boxShadow: t.iconShadow }}>
                  <Ic {...t.iconProps} />
                </div>
                <div className="flex-1 min-w-0">
                  {eyebrow ? (
                    <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[8.5px] font-extrabold tracking-[.1em] uppercase mb-0.5 ${t.chip}`}>{eyebrow}</div>
                  ) : null}
                  <div className="text-[15px] font-bold text-[var(--ink)] tracking-[-0.2px]">{title}</div>
                  {subtitle ? <div className="text-[10.5px] text-[var(--gray2)] truncate">{subtitle}</div> : null}
                </div>
                {action ? (
                  <button onClick={action.onClick} data-testid={action.testId}
                    className="btn-ink inline-flex items-center gap-1.5 flex-shrink-0">
                    {action.icon}{action.label}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })()
      )}
    </div>
  );
}
